import { Theme } from '@afterpaytouch/core'
import {
  THEME_COOKIE_NAME,
  SetThemeRequest,
  ConvergenceRollout,
  ConvergenceAvailable,
  Country,
  ConvergenceCountryTheme,
  DEFAULT_THEME,
  ConvergenceUnavailable,
  getPortalMigrationEnabledCountry,
} from '../model'
import { destroyCookie, parseCookies, setCookie } from 'nookies'
import { isUndefinedOrEmpty } from '@afterpay/utils'
import { GetServerSidePropsContext } from 'next'
import { app } from '../env'

export const isConvergenceEnabled = (rollout: ConvergenceRollout): boolean => {
  const result = Object.values(ConvergenceAvailable).includes(rollout as ConvergenceAvailable)
  console.log('[isConvergenceEnabled] Rollout:', rollout, 'Result:', result)
  return result
}

export const getFallbackTheme = (): Theme => {
  const result = app.COLD_START_THEME.length > 0 && isValidThemeValue(app.COLD_START_THEME) ? app.COLD_START_THEME : DEFAULT_THEME
  console.log('[getFallbackTheme] COLD_START_THEME:', app.COLD_START_THEME, 'Result:', result)
  return result
}

export const setConvergenceState = (country: Country, convergenceRollout: ConvergenceRollout, context: GetServerSidePropsContext): void => {
  console.log('[setConvergenceState] Country:', country, 'Rollout:', convergenceRollout)

  if (convergenceRollout === ConvergenceAvailable.Enabled || convergenceRollout === ConvergenceUnavailable.DisabledWithOverride) {
    const themeToSet = getThemeByLocale(country)
    console.log('[setConvergenceState] Setting theme:', themeToSet, 'for country:', country)
    updateTheme({ theme: themeToSet }, context)
    return
  }

  console.log('[setConvergenceState] Destroying theme cookie - rollout not enabled/override')
  destroyCookie(null, THEME_COOKIE_NAME)
}

export const getPortalMigrationEnabledFromCookie = (): boolean => {
  const cookies = parseCookies()
  const loginMigrationEnabledInCookie = cookies?.['consumer-portal-login-migration-variant'] ?? 'false'
  const loginMigrationEnabled = loginMigrationEnabledInCookie.toString() === 'true'
  return loginMigrationEnabled
}

export const getPortalMigrationEnabled = (country: Country): boolean => {
  const portalMigrationEnabledCountry = getPortalMigrationEnabledCountry()
  const isPortalMigrationEnabledByCountry = portalMigrationEnabledCountry.includes(country)
  if (!isPortalMigrationEnabledByCountry) {
    return false
  }

  return getPortalMigrationEnabledFromCookie()
}

export const setPortalMigrationState = (loginMigrationValue: string, context: GetServerSidePropsContext, country: Country): void => {
  const portalMigrationEnabledCountry = getPortalMigrationEnabledCountry()
  const isPortalMigrationEnabledByCountry = portalMigrationEnabledCountry.includes(country)
  const now = Date.now()
  if (!isPortalMigrationEnabledByCountry) {
    setCookie(context, 'consumer-portal-login-migration-variant', 'false', {
      path: '/',
      sameSite: 'lax',
      expires: new Date(now + 1000 * 60 * 60 * 24 * 365),
    })
    return
  }
  if (loginMigrationValue === 'A') {
    setCookie(context, 'consumer-portal-login-migration-variant', 'true', {
      path: '/',
      sameSite: 'lax',
      expires: new Date(now + 1000 * 60 * 60 * 24 * 365),
    })
  }
}

export const getActiveTheme = (
  { cookies }: Pick<GetServerSidePropsContext['req'], 'cookies' | 'url'>,
  country: Country,
  convergenceRollout: ConvergenceRollout
): Theme => {
  console.log('[getActiveTheme] Input - Country:', country, 'Rollout:', convergenceRollout)
  console.log('[getActiveTheme] Available cookies:', Object.keys(cookies))
  console.log('[getActiveTheme] Theme cookie value:', cookies[THEME_COOKIE_NAME])

  const themeByLocale = getThemeByLocale(country)
  console.log('[getActiveTheme] Theme by locale:', themeByLocale)

  if (convergenceRollout === ConvergenceUnavailable.DisabledWithOverride) {
    const storedTheme = cookies[THEME_COOKIE_NAME]
    const isValidStored = isValidThemeValue(storedTheme)
    const result = isValidStored ? storedTheme : DEFAULT_THEME
    console.log('[getActiveTheme] DisabledWithOverride - Stored theme:', storedTheme, 'Valid:', isValidStored, 'Result:', result)
    return result
  }

  const result = convergenceRollout === ConvergenceAvailable.Enabled ? themeByLocale : DEFAULT_THEME
  console.log('[getActiveTheme] Standard flow - Enabled?', convergenceRollout === ConvergenceAvailable.Enabled, 'Result:', result)
  return result
}

export const getThemeByLocale = (country: Country | undefined): Theme => {
  const result = country ? ConvergenceCountryTheme[country] : DEFAULT_THEME
  console.log('[getThemeByLocale] Country:', country, 'Result:', result)
  console.log('[getThemeByLocale] ConvergenceCountryTheme mapping:', ConvergenceCountryTheme)
  return result
}

export const updateTheme = async (data: SetThemeRequest, context: GetServerSidePropsContext): Promise<void> => {
  console.log('[updateTheme] Setting theme cookie:', data.theme)
  try {
    setCookie(context, THEME_COOKIE_NAME, data.theme, {
      path: '/',
      secure: true,
      sameSite: 'none',
    })
    console.log('[updateTheme] Successfully set theme cookie:', data.theme)
  } catch (error) {
    console.error('[updateTheme] Failed to set theme cookie:', error)
    console.error('Failed to set theme')
  }
}

export const isValidThemeValue = (value?: string): value is Theme => {
  const result = !isUndefinedOrEmpty(value) && Object.values(Theme).includes(value as Theme)
  console.log('[isValidThemeValue] Input:', value, 'Valid:', result, 'Available themes:', Object.values(Theme))
  return result
}
